/**
 * Employee Permissions Hook
 *
 * Provides React hooks for checking employee permissions in components
 */

import { useEffect, useState } from 'react';
import { getClient } from '@/lib/supabase/client';
import type { EmployeeProfile } from '@/lib/auth/employee-permissions';
import {
  canEmployeeAccessRoute,
  isOwnerOnlyRoute,
  EMPLOYEE_ALLOWED_ROUTES,
  OWNER_ONLY_ROUTES
} from '@/lib/auth/employee-permissions';

interface EmployeePermissionsState {
  profile: EmployeeProfile | null;
  isEmployee: boolean;
  isTenantOwner: boolean;
  isLoading: boolean;
  canAccessRoute: (route: string) => boolean;
  isRouteOwnerOnly: (route: string) => boolean;
}

const AUTH_SESSION_TIMEOUT_MS = 5000;

function getSessionWithTimeout(
  supabase: ReturnType<typeof getClient>,
  ms: number = AUTH_SESSION_TIMEOUT_MS
) {
  return Promise.race([
    supabase.auth.getSession(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('auth-session-timeout')), ms),
    ),
  ]);
}

/**
 * Hook for checking employee permissions in React components
 */
export function useEmployeePermissions(): EmployeePermissionsState {
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadEmployeeProfile() {
      try {
        const supabase = getClient();
        // getSession() reads the token locally (no network round-trip). The
        // AuthGuard already validates the session server-side, so we don't need
        // getUser()'s extra /auth/v1/user call just to read user.id here.
        const { data: { session }, error: authError } = await getSessionWithTimeout(supabase);
        const user = session?.user;

        if (authError || !user) {
          if (mounted) {
            setProfile(null);
            setIsLoading(false);
          }
          return;
        }

        // Get employee profile (use maybeSingle - user might not be an employee)
        const { data, error } = await supabase
          .from('employee_profiles')
          .select('id, user_id, tenant_id, role, is_active')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .is('deleted_at', null)
          .maybeSingle();

        if (mounted) {
          if (error || !data) {
            setProfile(null);
          } else {
            setProfile(data as EmployeeProfile);
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading employee profile:', error);
        if (mounted) {
          setProfile(null);
          setIsLoading(false);
        }
      }
    }

    loadEmployeeProfile();

    return () => {
      mounted = false;
    };
  }, []);

  const isEmployee = profile?.role === 'employee';
  const isTenantOwner = profile?.role === 'tenant_owner';

  const canAccessRoute = (route: string): boolean => {
    // Tenant owners can access everything
    if (isTenantOwner) return true;

    // Employees can only access allowed routes
    if (isEmployee) {
      return canEmployeeAccessRoute(route);
    }

    // No profile = allow access (might be super_admin or other user type)
    // The middleware and API guards will handle actual permission checks
    return true;
  };

  const isRouteOwnerOnly = (route: string): boolean => {
    return isOwnerOnlyRoute(route);
  };

  return {
    profile,
    isEmployee,
    isTenantOwner,
    isLoading,
    canAccessRoute,
    isRouteOwnerOnly,
  };
}

/**
 * Hook for simple employee check (without full profile)
 */
export function useIsEmployee(): boolean {
  const { isEmployee } = useEmployeePermissions();
  return isEmployee;
}

/**
 * Hook for simple tenant owner check
 */
export function useIsTenantOwner(): boolean {
  const { isTenantOwner } = useEmployeePermissions();
  return isTenantOwner;
}
